/**
 * Data layer for `igaming-mass-payout-processing`. Thin typed fetch wrappers over /api/v1/igaming-mass-payout-processing.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingMassPayoutProcessingRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingMassPayoutProcessingResponse {
	count: number;
	items: IgamingMassPayoutProcessingRow[];
}

export interface CreateIgamingMassPayoutProcessingBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-mass-payout-processing";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingMassPayoutProcessingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingMassPayoutProcessingResponse>(res);
}

export async function create(body: CreateIgamingMassPayoutProcessingBody): Promise<IgamingMassPayoutProcessingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingMassPayoutProcessingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingMassPayoutProcessingQueryKeys = {
	all: ["igaming-mass-payout-processing"] as const,
	list: () => [...igamingMassPayoutProcessingQueryKeys.all] as const,
	detail: (id: string) => [...igamingMassPayoutProcessingQueryKeys.all, id] as const,
};
